import { LocalStorageService } from "./local-storage-service";

/**
 * Beyaz etiket markası — artık configuration.json'dan değil API'den (DB) gelir.
 *
 * Neden: publish sunucudaki configuration.json'ı koruduğu için marka değişiklikleri deploy'a binmiyor,
 * favicon/logo eski kalıyordu. Marka veri olunca bu hata sınıfı yapısal olarak ortadan kalkıyor.
 *
 * Dayanıklılık kuralları:
 *   - Son bilinen marka localStorage'a yazılır → sonraki açılışta ANINDA boyanır, ağ beklenmez (FOUC yok).
 *   - API ulaşılamazsa önce cache, o da yoksa GÖMÜLÜ VARSAYILAN (bizim RIS markamız) kullanılır.
 *     Uygulama hiçbir koşulda markasız açılmaz.
 *   - Kiracının yüklemediği her varlık için bizim varsayılanımıza düşülür (kısmi beyaz etiket mümkün).
 */

const CACHE_KEY = "branding";

/** Gömülü varsayılan: kurulumda hiç marka yoksa / API yoksa uygulama bununla açılır. */
export const DEFAULT_BRANDING = {
    displayName: "",
    title: { tr: "RIS · eCloudPACS", en: "RIS · eCloudPACS" },
    description: { tr: "RIS · eCloudPACS", en: "RIS · eCloudPACS" },
    assets: {},          // boş → aşağıdaki gömülü varlıklar kullanılır
    supportWhatsApp: "",
    supportCategories: [],
};

/** Kiracı yüklememişse kullanılacak kendi varlıklarımız (build içinde gelir). */
export const OWN_ASSETS = {
    headerLogo: "/logo-ris.svg",          // koyu header için
    loginLogo: "/logo-ris.svg",
    loginBackground: "/login-bg.webp",
    favicon: "/favicon-ris.svg",
};

let current = DEFAULT_BRANDING;

const apiBase = () => window.AppSettings?.server?.api ?? "";

/** Görsel URL'i mutlak hale getirir: API göreli yol döner (/api/PublicBranding/Asset/12?v=hash). */
const absolute = (url) => (url?.startsWith("/") ? `${apiBase()}${url}` : url);

function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeCache(branding) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(branding));
    } catch {
        /* kota dolabilir; marka cache'i kritik değil */
    }
}

/** Uygulamanın kullandığı okuma yüzeyi. */
export const Branding = {
    /** Kiracı adı (yoksa boş) — footer/başlık. */
    displayName: () => current.displayName || "",

    /** Kiracının bir varlığı var mı? (header'da kendi logomuzu yan yana göstermek için gerekir) */
    has: (kind) => Boolean(current.assets?.[kind]),

    /** Varlık URL'i: kiracının yüklediği → yoksa bizim gömülü varsayılanımız. */
    asset: (kind) => absolute(current.assets?.[kind]) || OWN_ASSETS[kind],

    /** Yalnız kiracının yüklediği varlık (bizimkine düşmez); yoksa null. */
    tenantAsset: (kind) => (current.assets?.[kind] ? absolute(current.assets[kind]) : null),

    title: (lang) => current.title?.[lang] || current.title?.tr || DEFAULT_BRANDING.title.tr,
    description: (lang) => current.description?.[lang] || current.description?.tr || "",

    /**
     * Logonun arkasına konacak zemin rengi (yoksa null).
     * Kiracının logosu şeffaf değilse (beyaz zeminli PNG gibi) kendi dikdörtgeniyle gelir ve
     * slate header'da / beyaz kartta yama gibi durur; o rengin kutusuna alınınca kasıtlı görünür.
     * Yalnız KİRACININ logosu için uygulanır — kendi SVG logolarımız zaten şeffaf.
     */
    logoBackground: (kind) => {
        if (!current.assets?.[kind]) return null;   // bizim logomuz → zemin kutusu yok
        return kind === "headerLogo"
            ? current.headerLogoBackground || null
            : current.loginLogoBackground || null;
    },

    /** WhatsApp destek numarası (yalnız rakam). Boşsa destek butonu gösterilmez. */
    supportWhatsApp: () => current.supportWhatsApp || "",

    /** Destek formundaki kategoriler — Marka → WhatsApp sekmesinde satır satır girilir. */
    supportCategories: () => current.supportCategories || [],
};

/** Başlık, meta açıklama ve favicon'u markadan uygula. */
export function applyBranding() {
    const lang = LocalStorageService.getLanguage("tr");

    document.title = Branding.title(lang);
    document
        ?.querySelector('meta[name="description"]')
        ?.setAttribute("content", Branding.description(lang));

    const favicon = Branding.asset("favicon");
    document.querySelector('link[rel="icon"]')?.setAttribute("href", favicon);
    document.querySelector('link[rel="apple-touch-icon"]')?.setAttribute("href", favicon);
}

/**
 * Markayı yükler. Önce cache ile boyar (anında), sonra API'den doğrular.
 * API başarısızsa cache/gömülü varsayılanla devam eder — uygulama açılışını ASLA bloklamaz.
 */
export async function loadBranding() {
    const cached = readCache();
    if (cached) {
        current = cached;
        applyBranding();
    }

    try {
        const res = await fetch(`${apiBase()}/api/PublicBranding/Get`, { cache: "no-store" });
        if (res.ok) {
            const body = await res.json();
            const data = body?.data ?? body;
            if (data) {
                current = {
                    displayName: data.displayName ?? "",
                    title: data.title ?? DEFAULT_BRANDING.title,
                    description: data.description ?? DEFAULT_BRANDING.description,
                    assets: data.assets ?? {},
                    headerLogoBackground: data.headerLogoBackground ?? null,
                    loginLogoBackground: data.loginLogoBackground ?? null,
                    supportWhatsApp: data.supportWhatsApp ?? "",
                    supportCategories: data.supportCategories ?? [],
                };
                writeCache(current);
            }
        }
    } catch {
        // ağ yok / API kapalı → cache ya da gömülü varsayılan zaten yüklü
    }

    applyBranding();
    window.AppBranding = current;   // hata ayıklama ve non-React tüketiciler için
    return current;
}
